import React, { FC } from "react";
import styles from "./styles.module.less";
import { useTranslation } from "react-i18next";
import { getImgSrc } from "@/utils";
import dayjs from "dayjs";
import { Button } from "antd";
import { useMediaSize } from "@/hooks/useMediaSize";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from '@ant-design/icons';
const { confirm } = Modal;
export interface PrincipalAuthentication {
  userPoolId: string;

  userId: string;

  /**
   * 主体类型（个人为 P，企业为 E）
   */
  principalType: string;

  /**
   * 主体名称（个人主体存姓名，企业主体存企业名称）
   */
  principalName: string;

  /**
   * 主体编号（个人主体存身份证号，企业主体存统一社会信用代码）
   */
  principalCode: string;

  /**
   * 认证时间
   */
  authenticationTime: Date;

  toEnterpriseAuthentication: Function;
  unBindPersonalAuthentication: Function;
  unBindEnterpriseAuthentication: Function;
}

/**
 * 个人认证信息展示框
 */
const PersonalAuthenticationContainer: FC<{
  principalCode: string;
  principalName: string;
  authenticationTime: Date;
  toEnterpriseAuthentication: Function;
  unBindPersonalAuthentication: Function;
}> = ({ principalCode, principalName, authenticationTime, toEnterpriseAuthentication, unBindPersonalAuthentication }) => {
  const { t } = useTranslation();

  const { isPhoneMedia } = useMediaSize();
  const showConfirm = () => {
    confirm({
      title: t("common.confirmUnbind"),
      icon: <ExclamationCircleOutlined />,
      okText: t("common.confirm"),
      okType: 'danger',
      cancelText: t("common.cancel"),
      onOk() {
        unBindPersonalAuthentication();
      },
    });
  };
  return (
    <div className={styles.principalTypeList} style={{ height: 155 }}>
      <div className={styles.principalTypeImgContainer}>
        <img className={styles.principalTypeImg} src={getImgSrc("/personalAuthentication.png")} alt="" />
        {isPhoneMedia && (
          <span className={styles.principalTypeTitle}>{t("user.alreadyCompletedPersonalAuthentication")}</span>
        )}
      </div>
      <div className={styles.principalTypeContent}>
        {isPhoneMedia || (
          <span className={styles.principalTypeTitle}>{t("user.alreadyCompletedPersonalAuthentication")}</span>
        )}

        <div className={styles.principalTypeDescription}>
          <span>{t("user.principalAuthenticationType") + "："}</span>
          <span>{t("user.personal")}</span>
        </div>
        <div className={styles.principalTypeDescription}>
          <span>{t("user.realName") + "："}</span>
          <span>{principalName}</span>
        </div>
        <div className={styles.principalTypeDescription}>
          <span>{t("user.idCard") + "："}</span>
          <span>{principalCode}</span>
        </div>
        <div className={styles.principalTypeDescription}>
          <span>{t("user.authenticationTime") + "："}</span>
          <span>{dayjs(authenticationTime).format("YYYY-MM-DD HH:mm:ss")}</span>
        </div>
      </div>
      <div className={styles.changeOpsButtonContainer}>
        <Button type={isPhoneMedia ? "primary" : "link"} onClick={showConfirm}>
          {t("user.unbind") + " >>"}
        </Button>
        <Button type={isPhoneMedia ? "primary" : "link"} onClick={() => toEnterpriseAuthentication()}>
          {t("user.changeToEnterpriseAuthentication") + " >>"}
        </Button>
      </div>
    </div>
  );
};

/**
 * 企业认证信息展示框
 */
const EnterpriseAuthenticationContainer: FC<{
  principalCode: string;
  principalName: string;
  authenticationTime: Date;
  unBindEnterpriseAuthentication: Function
}> = ({ principalCode, principalName, authenticationTime, unBindEnterpriseAuthentication }) => {
  const { t } = useTranslation();

  const { isPhoneMedia } = useMediaSize();
  const showConfirm = () => {
    confirm({
      title: t("common.confirmUnbind"),
      icon: <ExclamationCircleOutlined />,
      okText: t("common.confirm"),
      okType: 'danger',
      cancelText: t("common.cancel"),
      onOk() {
        unBindEnterpriseAuthentication();
      },
    });
  };
  return (
    <div className={styles.principalTypeList} style={{ height: 155 }}>
      <div className={styles.principalTypeImgContainer}>
        <img className={styles.principalTypeImg} src={getImgSrc("/enterpriseAuthentication.png")} alt="" />
        {isPhoneMedia && (
          <span className={styles.principalTypeTitle}>{t("user.alreadyCompletedEnterpriseAuthentication")}</span>
        )}
      </div>
      <div className={styles.principalTypeContent}>
        {isPhoneMedia || (
          <span className={styles.principalTypeTitle}>{t("user.alreadyCompletedEnterpriseAuthentication")}</span>
        )}
        <div className={styles.principalTypeDescription}>
          <span>{t("user.principalAuthenticationType") + "："}</span>
          <span>{t("user.enterprise")}</span>
        </div>
        <div className={styles.principalTypeDescription}>
          <span>{t("user.enterpriseName") + "："}</span>
          <span>{principalName}</span>
        </div>
        <div className={styles.principalTypeDescription}>
          <span>{t("user.idCard") + "："}</span>
          <span>{principalCode}</span>
        </div>
        <div className={styles.principalTypeDescription}>
          <span>{t("user.authenticationTime") + "："}</span>
          <span>{dayjs(authenticationTime).format("YYYY-MM-DD HH:mm:ss")}</span>
        </div>
      </div>
      <div className={styles.changeOpsButtonContainer}>
        <Button type={isPhoneMedia ? "primary" : "link"} onClick={() => showConfirm()}>
          {t("user.unbind") + " >>"}
        </Button>
      </div>
    </div>
  );
};

/**
 * 主体已认证
 */
export const HadPrincipal: FC<PrincipalAuthentication> = ({
  userPoolId,
  userId,
  principalType,
  principalCode,
  principalName,
  authenticationTime,
  toEnterpriseAuthentication,
  unBindPersonalAuthentication,
  unBindEnterpriseAuthentication,
}) => {
  const { t } = useTranslation();

  const { isPhoneMedia } = useMediaSize();
  return (
    <>
      <div className={styles.authenticationWrapper}>
        <div className={styles.authenticationWrapperHeader}>
          {isPhoneMedia || <h3>{t("user.authenticationInfo")}</h3>}
          {principalType === "P" ? (
            <PersonalAuthenticationContainer
              principalCode={principalCode}
              principalName={principalName}
              authenticationTime={authenticationTime}
              toEnterpriseAuthentication={toEnterpriseAuthentication}
              unBindPersonalAuthentication={unBindPersonalAuthentication}
            />
          ) : (
            <EnterpriseAuthenticationContainer
              principalCode={principalCode}
              principalName={principalName}
              authenticationTime={authenticationTime}
              unBindEnterpriseAuthentication={unBindEnterpriseAuthentication}
            />
          )}
        </div>
      </div>
      <Modal

      >

      </Modal>
    </>
  );
};
